/**
 * SpatialCullingManager
 * 
 * Distance-based spatial culling system that enables/disables meshes
 * based on their distance from the player. Works alongside Babylon.js built-in
 * frustum culling to reduce GPU and CPU load for distant objects.
 * 
 * NOTE: Light culling is now handled by UnifiedLightManager.
 * 
 * Usage:
 * 1. Instantiate during level build with scene and default cull distance
 * 2. Register meshes that should be culled
 * 3. Call update(playerPosition) each frame
 * 4. Access stats via getStats() for debug UI
 */

import { Vector3, Frustum, Matrix, type Scene, type AbstractMesh, type Camera } from '@babylonjs/core';
import { logger } from '../core/Logger';

/**
 * Statistics about culling state for debug display
 */
export interface CullingStats {
  /** Total meshes registered for distance culling */
  totalMeshes: number;
  /** Meshes currently within cull distance */
  visibleMeshes: number;
  /** Meshes currently outside cull distance (disabled) */
  culledMeshes: number;
  /** Total lights registered for distance culling */
  totalLights: number;
  /** Lights currently within cull distance */
  visibleLights: number;
  /** Lights currently outside cull distance (disabled) */
  culledLights: number;
  /** Current cull distance in meters */
  cullDistance: number;
  /** Frustum far plane distance for culling */
  frustumFarPlane: number;
  /** Whether culling is enabled */
  enabled: boolean;
}

/**
 * Internal entry for tracking mesh culling state
 */
interface MeshEntry {
  mesh: AbstractMesh;
  /** Cached enabled state before culling was applied (to restore on dispose) */
  originalEnabled: boolean;
}

export class SpatialCullingManager {
  private scene: Scene;
  private cullDistance: number;
  private frustumFarPlane: number;
  private enabled: boolean = true;
  
  /** Maximum number of lights that can be enabled simultaneously to avoid shader recompilation */
  private maxSimultaneousLights: number;
  
  /** 
   * Optional game camera for frustum checks.
   * When set, frustum culling uses this camera instead of scene.activeCamera.
   * This allows free-roam mode to still cull based on the game camera's view.
   */
  private gameCamera: Camera | null = null;
  
  private meshEntries: Map<AbstractMesh, MeshEntry> = new Map();
  
  /** Previous frame visible count for rapid change detection */
  private prevVisibleMeshes: number = 0;
  
  /** Track mesh visibility state changes for debugging */
  private meshVisibilityState: Map<AbstractMesh, boolean> = new Map();
  
  /** Threshold for logging rapid visibility changes (number of objects) */
  private static readonly RAPID_CHANGE_THRESHOLD = 5;
  
  private stats: CullingStats = {
    totalMeshes: 0,
    visibleMeshes: 0,
    culledMeshes: 0,
    totalLights: 0,
    visibleLights: 0,
    culledLights: 0,
    cullDistance: 30,
    frustumFarPlane: 400,
    enabled: true,
  };

  /**
   * Create a new SpatialCullingManager
   * @param scene The Babylon.js scene for frustum culling checks
   * @param defaultCullDistance Default distance in meters (typically 30m)
   * @param maxLights Maximum simultaneous lights (default 8) - DEPRECATED, lights now managed by UnifiedLightManager
   * @param frustumFarPlane Far plane distance for frustum culling (default 50m)
   */
  constructor(scene: Scene, defaultCullDistance: number = 30, maxLights: number = 8, frustumFarPlane: number = 400) {
    this.scene = scene;
    this.cullDistance = defaultCullDistance;
    this.frustumFarPlane = frustumFarPlane;
    this.maxSimultaneousLights = maxLights;
    this.stats.cullDistance = defaultCullDistance;
    this.stats.frustumFarPlane = frustumFarPlane;
    
    // NOTE: Light management has been moved to UnifiedLightManager.
    // SpatialCullingManager now only handles mesh culling.
    
    logger.core.debug(`SpatialCullingManager initialized with cull distance: ${defaultCullDistance}m, frustum far: ${frustumFarPlane}m (lights managed by UnifiedLightManager)`);
  }

  /**
   * Get the maximum simultaneous lights setting.
   */
  getMaxSimultaneousLights(): number {
    return this.maxSimultaneousLights;
  }

  /**
   * Register a mesh for distance-based culling.
   * The mesh will be disabled when the player is beyond the cull distance.
   * 
   * @param mesh The mesh to register
   */
  registerMesh(mesh: AbstractMesh): void {
    if (this.meshEntries.has(mesh)) {
      return; // Already registered
    }
    
    this.meshEntries.set(mesh, {
      mesh,
      originalEnabled: mesh.isEnabled(),
    });
    
    this.stats.totalMeshes = this.meshEntries.size;
  }

  /**
   * Unregister a mesh from culling.
   * Restores the mesh to its original enabled state.
   * 
   * @param mesh The mesh to unregister
   */
  unregisterMesh(mesh: AbstractMesh): void {
    const entry = this.meshEntries.get(mesh);
    if (!entry) return;
    
    // Restore original state
    mesh.setEnabled(entry.originalEnabled);
    this.meshEntries.delete(mesh);
    
    this.stats.totalMeshes = this.meshEntries.size;
  }

  /**
   * Set the cull distance in meters.
   * Objects beyond this distance from the player will be disabled.
   * 
   * @param meters Cull distance in meters (typically 5-100)
   */
  setCullDistance(meters: number): void {
    this.cullDistance = Math.max(1, meters);
    this.stats.cullDistance = this.cullDistance;
    
    logger.core.debug(`SpatialCullingManager: cull distance set to ${this.cullDistance}m`);
  }

  /**
   * Get the current cull distance in meters.
   */
  getCullDistance(): number {
    return this.cullDistance;
  }

  /**
   * Set the frustum far plane distance in meters.
   * Objects beyond this distance from the camera will be culled.
   * 
   * @param meters Far plane distance in meters
   */
  setFrustumFarPlane(meters: number): void {
    this.frustumFarPlane = Math.max(1, meters);
    this.stats.frustumFarPlane = this.frustumFarPlane;
    
    logger.core.debug(`SpatialCullingManager: frustum far plane set to ${this.frustumFarPlane}m`);
  }

  /**
   * Get the current frustum far plane distance in meters.
   */
  getFrustumFarPlane(): number {
    return this.frustumFarPlane;
  }

  /**
   * Enable or disable distance-based culling.
   * When disabled, all registered objects are re-enabled.
   * 
   * @param enabled Whether culling should be active
   */
  setEnabled(enabled: boolean): void {
    if (this.enabled === enabled) return;
    
    this.enabled = enabled;
    this.stats.enabled = enabled;
    
    if (!enabled) {
      // Re-enable all registered objects
      for (const entry of this.meshEntries.values()) {
        entry.mesh.setEnabled(entry.originalEnabled);
      }
      // Re-enable all lights when culling is disabled
      for (const light of this.scene.lights) {
        if (!light.isDisposed()) {
          light.setEnabled(true);
        }
      }
      
      // Update stats to reflect all visible
      this.stats.visibleMeshes = this.stats.totalMeshes;
      this.stats.culledMeshes = 0;
      this.stats.visibleLights = this.scene.lights.length;
      this.stats.culledLights = 0;
    }
    
    logger.core.debug(`SpatialCullingManager: ${enabled ? 'enabled' : 'disabled'}`);
  }

  /**
   * Check if distance culling is enabled.
   */
  isEnabled(): boolean {
    return this.enabled;
  }

  /**
   * Set the game camera to use for frustum checks.
   * When set, frustum culling uses this camera instead of scene.activeCamera.
   * This allows free-roam mode to still cull based on the game camera's view,
   * so you can fly around and see what gets culled off-screen.
   * 
   * @param camera The game camera to use, or null to use scene.activeCamera
   */
  setGameCamera(camera: Camera | null): void {
    this.gameCamera = camera;
  }

  /**
   * Get the current game camera used for frustum checks.
   */
  getGameCamera(): Camera | null {
    return this.gameCamera;
  }

  /**
   * Update culling state based on player position.
   * Call this once per frame in the render loop.
   * 
   * Uses frustum-based culling: objects must be BOTH within the frustum far plane
   * AND inside the camera frustum to be visible. Objects outside the frustum
   * are culled even if they're close to the player.
   * 
   * @param playerPosition Current world position of the player
   */
  update(playerPosition: Vector3): void {
    if (!this.enabled) return;
    
    // Use game camera for frustum checks if set, otherwise fall back to active camera
    const camera = this.gameCamera ?? this.scene.activeCamera;
    let visibleMeshes = 0;
    let culledMeshes = 0;
    
    // Compute frustum planes with custom far plane for culling
    const frustumPlanes = camera ? this.computeCullingFrustum(camera) : null;
    
    // Process meshes
    for (const entry of this.meshEntries.values()) {
      const mesh = entry.mesh;
      
      // Skip disposed meshes
      if (mesh.isDisposed()) {
        this.meshEntries.delete(mesh);
        continue;
      }
      
      // Force world matrix update to ensure bounding info is current
      // This is crucial because culled meshes may have stale transforms
      mesh.computeWorldMatrix(true);
      
      // Get world-space bounding sphere - this is the actual geometric center, not the pivot
      const boundingInfo = mesh.getBoundingInfo();
      if (!boundingInfo) {
        // No bounding info - use frustum check only
        if (frustumPlanes) {
          const meshPos = mesh.getAbsolutePosition();
          const distToCamera = camera ? Vector3.Distance(camera.position, meshPos) : 0;
          if (distToCamera <= this.frustumFarPlane) {
            if (entry.originalEnabled && !mesh.isEnabled()) {
              mesh.setEnabled(true);
            }
            visibleMeshes++;
          } else {
            if (mesh.isEnabled()) {
              mesh.setEnabled(false);
            }
            culledMeshes++;
          }
        } else {
          // No camera - keep visible
          if (entry.originalEnabled && !mesh.isEnabled()) {
            mesh.setEnabled(true);
          }
          visibleMeshes++;
        }
        continue;
      }
      
      // Use the bounding sphere's CENTER in world space, not the mesh pivot
      // This handles meshes where geometry is offset from origin
      const worldSphere = boundingInfo.boundingSphere;
      const sphereCenter = worldSphere.centerWorld;
      const sphereRadius = worldSphere.radiusWorld;
      
      // Validate bounding sphere values - NaN/Infinity means corrupted bounds
      if (!isFinite(sphereCenter.x) || !isFinite(sphereCenter.y) || !isFinite(sphereCenter.z) || !isFinite(sphereRadius)) {
        // Corrupted bounding info - keep visible to be safe
        if (entry.originalEnabled && !mesh.isEnabled()) {
          mesh.setEnabled(true);
        }
        visibleMeshes++;
        continue;
      }
      
      // Frustum-based culling: object must be inside the frustum to be visible
      let shouldBeVisible = false;
      
      if (frustumPlanes) {
        // Check if mesh is inside the custom culling frustum
        shouldBeVisible = mesh.isInFrustum(frustumPlanes);
      } else {
        // No camera/frustum available - fall back to distance-based culling from player
        const distToCenter = Vector3.Distance(playerPosition, sphereCenter);
        const effectiveDist = Math.max(0, distToCenter - sphereRadius);
        shouldBeVisible = effectiveDist <= this.cullDistance;
      }
      
      // Track visibility state change for debugging
      const wasVisible = this.meshVisibilityState.get(mesh);
      if (wasVisible !== undefined && wasVisible !== shouldBeVisible) {
        // Visibility toggled - track it
        (mesh as any).__cullingToggleCount = ((mesh as any).__cullingToggleCount ?? 0) + 1;
      }
      this.meshVisibilityState.set(mesh, shouldBeVisible);
      
      // Only change state if needed (avoid unnecessary state changes)
      if (shouldBeVisible) {
        // Only enable if it was originally enabled
        if (entry.originalEnabled && !mesh.isEnabled()) {
          mesh.setEnabled(true);
        }
        visibleMeshes++;
      } else {
        if (mesh.isEnabled()) {
          mesh.setEnabled(false);
        }
        culledMeshes++;
      }
    }
    
    // NOTE: Light gating is now handled entirely by UnifiedLightManager.
    // This manager only handles mesh culling.
    
    // Rapid change detection - log when visibility count changes dramatically
    // Skip first frame (prevVisible = 0) since that's just initial population
    const meshDelta = Math.abs(visibleMeshes - this.prevVisibleMeshes);
    
    if (this.prevVisibleMeshes > 0 && meshDelta > SpatialCullingManager.RAPID_CHANGE_THRESHOLD) {
      // Find meshes that toggled this frame
      const toggledMeshes: string[] = [];
      for (const [mesh] of this.meshEntries) {
        const toggleCount = (mesh as any).__cullingToggleCount ?? 0;
        if (toggleCount > 0) {
          toggledMeshes.push(`${mesh.name}(${toggleCount}x)`);
          (mesh as any).__cullingToggleCount = 0; // Reset
        }
      }
      
      logger.performance.warn(
        `RAPID VISIBILITY CHANGE at pos (${playerPosition.x.toFixed(1)}, ${playerPosition.y.toFixed(1)}, ${playerPosition.z.toFixed(1)}): ` +
        `meshes ${this.prevVisibleMeshes} -> ${visibleMeshes} (Δ${meshDelta})` +
        (toggledMeshes.length > 0 ? ` | Toggled: ${toggledMeshes.slice(0, 10).join(', ')}${toggledMeshes.length > 10 ? '...' : ''}` : '')
      );
    }
    
    this.prevVisibleMeshes = visibleMeshes;
    
    // Update stats
    this.stats.totalMeshes = this.meshEntries.size;
    this.stats.visibleMeshes = visibleMeshes;
    this.stats.culledMeshes = culledMeshes;
    // Light stats now come from UnifiedLightManager (just set to 0 here)
    this.stats.totalLights = 0;
    this.stats.visibleLights = 0;
    this.stats.culledLights = 0;
  }

  /**
   * Get current culling statistics for debug display.
   */
  getStats(): CullingStats {
    return { ...this.stats };
  }


  /**
   * Clean up and restore all objects to their original state.
   */
  dispose(): void {
    // Restore all meshes to original enabled state
    for (const entry of this.meshEntries.values()) {
      if (!entry.mesh.isDisposed()) {
        entry.mesh.setEnabled(entry.originalEnabled);
      }
    }
    
    // Re-enable all lights (no registration, just enable all)
    for (const light of this.scene.lights) {
      if (!light.isDisposed()) {
        light.setEnabled(true);
      }
    }
    
    this.meshEntries.clear();
    
    this.stats.totalMeshes = 0;
    this.stats.visibleMeshes = 0;
    this.stats.culledMeshes = 0;
    this.stats.totalLights = 0;
    this.stats.visibleLights = 0;
    this.stats.culledLights = 0;
    
    logger.core.debug('SpatialCullingManager disposed');
  }

  /**
   * Compute frustum planes with a custom far plane for culling.
   * This allows the culling frustum to have a different far plane than the camera's rendering frustum.
   */
  private computeCullingFrustum(camera: Camera): import('@babylonjs/core').Plane[] | null {
    // Validate camera position is not NaN/Infinity (can happen with degenerate spline tangents)
    const camPos = camera.position;
    if (!isFinite(camPos.x) || !isFinite(camPos.y) || !isFinite(camPos.z)) {
      return null;
    }
    
    // Get the camera's view matrix
    const viewMatrix = camera.getViewMatrix();
    
    // Create a custom projection matrix with our culling far plane
    const aspectRatio = this.scene.getEngine().getAspectRatio(camera);
    const fov = camera.fov ?? (Math.PI / 4);
    const nearPlane = camera.minZ ?? 0.1;
    const farPlane = this.frustumFarPlane;
    
    // Create perspective projection matrix with custom far plane
    const projectionMatrix = Matrix.PerspectiveFovLH(fov, aspectRatio, nearPlane, farPlane);
    
    // Combine view and projection matrices
    const viewProjection = viewMatrix.multiply(projectionMatrix);
    
    // Check if view-projection matrix is valid
    const m = viewProjection.m;
    if (!isFinite(m[0]) || !isFinite(m[5]) || !isFinite(m[10]) || !isFinite(m[15])) {
      return null;
    }
    
    return Frustum.GetPlanes(viewProjection);
  }
}
